import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Map as ImmutableMap } from "immutable";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ICellRendererParams } from "ag-grid-enterprise";
import { Button, Toolbar, Grid, Collapse } from "@material-ui/core";
import {
  ExpandLess,
  ExpandMore,
  TextFormat,
  UnfoldLess,
  UnfoldMore,
} from "@material-ui/icons";
import { Delta } from "../QueueLeaderboard";
import { HistoricalIvr, IvrState } from "./IvrHud";
import DurationRenderer from "./DurationRenderer";

interface Props {
  historicalIvr: ImmutableMap<string, HistoricalIvr>;
  ivrStates: IvrState[];
  ivrDeltas: Delta[];
  fontSize: string;
  defaultColDef: any;
  activeLeaderboard: "talking" | "interactions" | "statistics";
  theme: string;
  gridHeight: number;
  tab: string;
  excludedIvrs: string[];
}

interface IvrRowData {
  extensionId: string;
  name: string;
  transferred: number;
  abandoned: number;
  totalCalls: number;
  totalDuration: number;
  averageDuration: number;
  longestDuration: number;
  type?: "AIR" | "IVR";
}

function durationFormatter(params: { value: number | undefined }) {
  if (params?.value === undefined) return "00:00";
  return `${Math.floor(params.value / 60 / 1000)
    .toString()
    .padStart(2, "0")}:${Math.floor(
    (params.value - Math.floor(params.value / 60 / 1000) * 60 * 1000) / 1000
  )
    .toString()
    .padStart(2, "0")}`;
}

const IvrStatisticsLeaderboard: React.FC<Props> = React.memo(
  ({
    historicalIvr,
    ivrStates,
    ivrDeltas,
    fontSize,
    defaultColDef,
    activeLeaderboard,
    theme,
    gridHeight,
    tab,
    excludedIvrs,
  }: Props) => {
    const gridRef = useRef<AgGridReact>(null);
    const [gridReady, setGridReady] = useState(false);
    const [totalsRow, setTotalsRow] = useState<IvrRowData>({
      extensionId: "",
      name: "Totals",
      transferred: 0,
      abandoned: 0,
      totalCalls: 0,
      totalDuration: 0,
      averageDuration: 0,
      longestDuration: 0,
    });
    const [rowData] = useState(
      (() =>
        historicalIvr
          .valueSeq()
          .map((ivr) => ({
            extensionId: ivr.id,
            name: ivr.name,
            transferred: 0,
            abandoned: 0,
            totalCalls: 0,
            totalDuration: 0,
            averageDuration: 0,
            longestDuration: 0,
            type: ivr.isAir ? "AIR" : "IVR",
          }))
          .toArray())()
    );
    const columnDefs = useMemo(
      () => [
        {
          headerName: "Extension Id",
          field: "extensionId",
          filter: "agSetColumnFilter",
          //  ,
          hide: true,
        },
        {
          headerName: "Type",
          field: "type",
          hide: true,
          filter: "agTextColumnFilter",
          //  ,
          sortable: true,
        },
        {
          headerName: tab === "air" ? "AI Agent Name" : "IVR Name",
          field: "name",
          filter: "agSetColumnFilter",
          //  ,
          sortable: true,
        },
        {
          headerName: tab === "air" ? "Calls Ended" : "Calls Abandoned",
          field: "abandoned",
          filter: "agNumberColumnFilter",
          //  ,
          sortable: true,
        },
        {
          headerName: "Calls Transferred",
          field: "transferred",
          filter: "agNumberColumnFilter",
          //  ,
          sortable: true,
        },
        {
          headerName: "Total Interactions",
          field: "totalCalls",
          filter: "agNumberColumnFilter",
          //  ,
          sortable: true,
        },
        {
          headerName: "Total Duration",
          field: "totalDuration",
          filter: "agNumberColumnFilter",
          //  ,
          sortable: true,

          valueFormatter: durationFormatter,
        },
        {
          headerName: "Average Duration",
          field: "averageDuration",
          //  ,
          filter: "agNumberColumnFilter",
          valueFormatter: durationFormatter,
          sortable: true,
        },
        {
          headerName: "Longest Duration",
          field: "longestDuration",
          filter: "agNumberColumnFilter",
          //  ,
          valueFormatter: durationFormatter,
          sortable: true,
        },
      ],
      [tab]
    );

    // Calculate totals for visible rows
    const calculateTotals = () => {
      if (!gridRef.current?.api) return;

      const visibleRows: IvrRowData[] = [];
      gridRef.current.api.forEachNodeAfterFilter((node) => {
        visibleRows.push(node.data);
      });

      const totals: IvrRowData = visibleRows.reduce(
        (acc, row) => ({
          extensionId: "Totals",
          name: "Totals",
          transferred: acc.transferred + row.transferred,
          abandoned: acc.abandoned + row.abandoned,
          totalCalls: acc.totalCalls + row.totalCalls,
          totalDuration: acc.totalDuration + row.totalDuration,
          averageDuration: 0, // Placeholder, will be calculated separately
          longestDuration: Math.max(acc.longestDuration, row.longestDuration),
        }),
        {
          extensionId: "Totals",
          name: "Totals",
          transferred: 0,
          abandoned: 0,
          totalCalls: 0,
          totalDuration: 0,
          averageDuration: 0,
          longestDuration: 0,
        }
      );

      // Calculate average duration
      totals.averageDuration =
        totals.totalCalls > 0 ? totals.totalDuration / totals.totalCalls : 0;

      setTotalsRow(totals);
    };

    // Recalculate totals when filters change
    const onFilterChanged = (event: any) => {
    const newColumnState = event.api.getFilterModel();
      if (tab === "ivr") {
        if (activeLeaderboard === "talking") localStorage.setItem(`ivr-talking-filters`, JSON.stringify(newColumnState))
      }
      if (tab === "air") {
        if (activeLeaderboard === "talking") localStorage.setItem(`air-talking-filters`, JSON.stringify(newColumnState))
      }
            
      calculateTotals();
    };

    // Filter on IVR IDS
    useEffect(() => {
      if (!gridRef.current || !gridRef.current?.api || !gridReady) return;
      const filterInstance = gridRef.current.api.getFilterInstance("extensionId");
      if (filterInstance) {
        const inArray = Array.from(historicalIvr.filter((value, key) => !excludedIvrs.find((ivrId) => ivrId === key)).values())
        .map((data) => data.id);
        filterInstance.setModel({
          values: inArray,
        });
        gridRef.current.api.onFilterChanged();
      }
    }, [gridReady, excludedIvrs]);

    useEffect(() => {
      if (!gridReady || !gridRef.current?.api || !ivrStates.length) return;

      const updates: IvrRowData[] = [];

      ivrDeltas.forEach((delta) => {
        const node = gridRef.current?.api.getRowNode(delta.userId);
        if (!node) return;

        const { data } = node;
        const {
          ivrAbandonedCount = 0,
          duration = 0,
          maxDuration = 0,
          transferCount = 0,
        } = delta;

        const totalCalls = data.totalCalls + (ivrAbandonedCount + transferCount);
        const totalDuration = data.totalDuration + duration;
        const averageDuration = totalDuration / totalCalls;
        const longestDuration = Math.max(data.longestDuration, maxDuration);
        const transferred = data.transferred + transferCount;
        const abandoned = data.abandoned + ivrAbandonedCount;

        updates.push({
          extensionId: data.extensionId,
          name: data.name,
          type: data.type,
          totalCalls,
          totalDuration,
          averageDuration,
          longestDuration,
          transferred,
          abandoned,
        });
      });

      if (updates.length) {
        gridRef.current.api.applyTransaction({ update: updates });
        calculateTotals(); // Recalculate totals after updating data
      }
    }, [ivrDeltas, gridReady]);

    useEffect(() => {
      if (activeLeaderboard === "statistics") {
        if (tab === "ivr") {
        const columnModelFromStorage = localStorage.getItem(`ivr-statistics-columns`) || "{}"
        gridRef.current?.columnApi?.applyColumnState({
          state: JSON.parse(columnModelFromStorage),
          applyOrder: true
          })
        }
         if (tab === "air") {
          const columnModelFromStorage = localStorage.getItem(`air-statistics-columns`) || "{}"
          gridRef.current?.columnApi?.applyColumnState({
            state: JSON.parse(columnModelFromStorage),
            applyOrder: true
          })
         }
      }
    }, [gridReady, gridRef, activeLeaderboard, tab])

    useEffect(() => {
      if (!gridRef.current || !gridRef.current?.api) return;
      const currentFilterModel = gridRef.current.api.getFilterModel() || {};
      if (tab === "air") {
        currentFilterModel.type = {
          filterType: "text",
          type: "equals",
          filter: "AIR",
        };
      } else {
        currentFilterModel.type = {
          filterType: "text",
          type: "equals",
          filter: "IVR",
        };
      }
      gridRef.current.api.setFilterModel(currentFilterModel);
    }, [tab, gridReady]);

    return (
      <div
        className={theme}
        style={{
          height: `${gridHeight}vh`,
          width: "100%",
          display: activeLeaderboard === "statistics" ? "block" : "none",
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          onGridReady={() => setGridReady(true)}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          suppressLoadingOverlay
          getRowId={(params) => params.data.extensionId}
          onFilterChanged={onFilterChanged}
          onColumnResized={(e) => {
            const newColumnState = e.columnApi.getColumnState();
            if (tab === "ivr") {
              if (activeLeaderboard === "statistics") localStorage.setItem(`ivr-statistics-columns`, JSON.stringify(newColumnState))
            }
            if (tab === "air") {
              if (activeLeaderboard === "statistics") localStorage.setItem(`air-statistics-columns`, JSON.stringify(newColumnState))
            }
            
          }}
          onColumnMoved={(e) => {
            const newColumnState = e.columnApi.getColumnState();
            if (tab === "ivr") {
              if (activeLeaderboard === "statistics") localStorage.setItem(`ivr-statistics-columns`, JSON.stringify(newColumnState))
            }
            if (tab === "air") {
              if (activeLeaderboard === "statistics") localStorage.setItem(`air-statistics-columns`, JSON.stringify(newColumnState))
            }
            
          }}
          pinnedBottomRowData={[totalsRow]}
          rowHeight={28}
        />
      </div>
    );
  }
);

export default IvrStatisticsLeaderboard;
